/**
 * ScrollLockManager - Utility for managing body scroll locking with reference counting
 * Handles multiple modals and provides proper scroll position restoration
 * Optimized for performance with debouncing and efficient DOM manipulation
 */
class ScrollLockManager {
  constructor() {
    this.state = {
      isLocked: false,
      refCount: 0,
      savedScrollPosition: { x: 0, y: 0 },
      savedBodyStyles: {
        overflow: '',
        position: '',
        top: '',
        width: '',
        paddingRight: ''
      }
    };
    
    // Performance optimization: cache DOM elements and computed values
    this._cachedScrollbarWidth = null;
    this._cachedBody = null;
    this._pendingStyleUpdates = null;
    this._styleUpdateScheduled = false;
    
    // Debounced scroll position updates
    this._debouncedScrollUpdate = this._debounce(this._updateScrollPosition.bind(this), 16);
    
    // Bind methods to preserve context
    this.lock = this.lock.bind(this);
    this.unlock = this.unlock.bind(this);
    this.cleanup = this.cleanup.bind(this);
    
    // Add cleanup listener for page navigation
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.cleanup);
    }
  }

  /**
   * Lock body scroll and save current state
   */
  lock() {
    try {
      this.state.refCount++;
      
      // Only apply styles on first lock
      if (this.state.refCount === 1 && !this.state.isLocked) {
        this._saveCurrentState();
        this._applyScrollLock();
        this.state.isLocked = true;
      }
    } catch (error) {
      console.warn('ScrollLockManager: Failed to lock scroll', error);
      // Ensure ref count doesn't get out of sync on error
      this.state.refCount = Math.max(0, this.state.refCount - 1);
    }
  }

  /**
   * Unlock body scroll with reference counting
   */
  unlock() {
    try {
      this.state.refCount = Math.max(0, this.state.refCount - 1);
      
      // Only remove styles when no more references
      if (this.state.refCount === 0 && this.state.isLocked) {
        this._removeScrollLock();
        this._restoreScrollPosition();
        this.state.isLocked = false;
      }
    } catch (error) {
      console.warn('ScrollLockManager: Failed to unlock scroll', error);
    }
  }

  /**
   * Check if scroll is currently locked
   */
  isLocked() {
    return this.state.isLocked;
  }

  /**
   * Get current reference count
   */
  getRefCount() {
    return this.state.refCount;
  }

  /**
   * Force cleanup - removes all locks
   */
  cleanup() {
    try {
      if (this.state.isLocked) {
        this._removeScrollLock();
        this._restoreScrollPosition();
      }
      this.state.refCount = 0;
      this.state.isLocked = false;
    } catch (error) {
      console.warn('ScrollLockManager: Failed to cleanup', error);
    }
  }

  /**
   * Reset scroll lock state without restoring position - useful for navigation
   */
  reset() {
    try {
      if (this.state.isLocked) {
        this._removeScrollLock();
        // Don't restore scroll position on reset
      }
      this.state.refCount = 0;
      this.state.isLocked = false;
      // Reset saved position to top
      this.state.savedScrollPosition = { x: 0, y: 0 };
    } catch (error) {
      console.warn('ScrollLockManager: Failed to reset', error);
    }
  }

  /**
   * Save current scroll position and body styles
   * @private
   */
  _saveCurrentState() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Save scroll position
    this.state.savedScrollPosition = {
      x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0,
      y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    };

    // Save body styles
    const body = document.body;
    const computedStyle = window.getComputedStyle(body);
    
    this.state.savedBodyStyles = {
      overflow: body.style.overflow || '',
      position: body.style.position || '',
      top: body.style.top || '',
      width: body.style.width || '',
      paddingRight: body.style.paddingRight || ''
    };
  }

  /**
   * Apply scroll lock styles to body
   * @private
   */
  _applyScrollLock() {
    if (typeof document === 'undefined') return;

    const body = this._getCachedBody();
    const scrollbarWidth = this._getCachedScrollbarWidth();
    
    // Batch style updates for better performance
    this._batchStyleUpdate(() => {
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${this.state.savedScrollPosition.y}px`;
      body.style.width = '100%';
      
      // Compensate for scrollbar width to prevent layout shift
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    });

    // Add CSS class for additional styling
    body.classList.add('scroll-locked');

    // Handle iOS Safari specific issues
    this._handleIOSScrollLock();
  }

  /**
   * Remove scroll lock styles from body
   * @private
   */
  _removeScrollLock() {
    if (typeof document === 'undefined') return;

    const body = this._getCachedBody();
    
    // Batch style restoration for better performance
    this._batchStyleUpdate(() => {
      body.style.overflow = this.state.savedBodyStyles.overflow;
      body.style.position = this.state.savedBodyStyles.position;
      body.style.top = this.state.savedBodyStyles.top;
      body.style.width = this.state.savedBodyStyles.width;
      body.style.paddingRight = this.state.savedBodyStyles.paddingRight;
    });
    
    // Remove CSS class
    body.classList.remove('scroll-locked');
    
    // Clean up iOS specific handlers
    this._cleanupIOSScrollLock();
  }

  /**
   * Restore scroll position with performance optimization
   * @private
   */
  _restoreScrollPosition() {
    if (typeof window === 'undefined') return;

    const { x, y } = this.state.savedScrollPosition;

    // Use requestAnimationFrame to ensure DOM is ready and avoid layout thrashing
    const restoreScroll = () => {
      // Check if position actually needs to be restored
      const currentX = window.pageXOffset || document.documentElement.scrollLeft || 0;
      const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (currentX !== x || currentY !== y) {
        window.scrollTo(x, y);
      }
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(restoreScroll);
    } else {
      setTimeout(restoreScroll, 0);
    }
  }

  /**
   * Get cached body element for performance
   * @private
   */
  _getCachedBody() {
    if (!this._cachedBody && typeof document !== 'undefined') {
      this._cachedBody = document.body;
    }
    return this._cachedBody;
  }

  /**
   * Get cached scrollbar width for performance
   * @private
   */
  _getCachedScrollbarWidth() {
    if (this._cachedScrollbarWidth === null) {
      this._cachedScrollbarWidth = this._calculateScrollbarWidth();
    }
    return this._cachedScrollbarWidth;
  }

  /**
   * Calculate scrollbar width for layout shift compensation
   * @private
   */
  _calculateScrollbarWidth() {
    if (typeof document === 'undefined') return 0;

    // Use cached value if available
    if (this._cachedScrollbarWidth !== null) {
      return this._cachedScrollbarWidth;
    }

    // Create temporary div to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.cssText = 'visibility:hidden;overflow:scroll;position:absolute;top:-9999px;width:100px;height:100px';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    
    document.body.removeChild(outer);
    
    return scrollbarWidth;
  }

  /**
   * Batch style updates to minimize layout thrashing
   * @private
   */
  _batchStyleUpdate(updateFn) {
    if (this._styleUpdateScheduled) {
      // If an update is already scheduled, queue this one
      const previousUpdate = this._pendingStyleUpdates;
      this._pendingStyleUpdates = () => {
        if (previousUpdate) previousUpdate();
        updateFn();
      };
      return;
    }

    this._styleUpdateScheduled = true;
    this._pendingStyleUpdates = updateFn;

    // Use requestAnimationFrame for optimal timing
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        if (this._pendingStyleUpdates) {
          this._pendingStyleUpdates();
          this._pendingStyleUpdates = null;
        }
        this._styleUpdateScheduled = false;
      });
    } else {
      // Fallback for environments without requestAnimationFrame
      setTimeout(() => {
        if (this._pendingStyleUpdates) {
          this._pendingStyleUpdates();
          this._pendingStyleUpdates = null;
        }
        this._styleUpdateScheduled = false;
      }, 0);
    }
  }

  /**
   * Debounce utility for performance optimization
   * @private
   */
  _debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Update scroll position with debouncing
   * @private
   */
  _updateScrollPosition() {
    if (typeof window === 'undefined') return;

    this.state.savedScrollPosition = {
      x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0,
      y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    };
  }

  /**
   * Handle iOS Safari specific scroll lock issues
   * @private
   */
  _handleIOSScrollLock() {
    if (typeof document === 'undefined') return;

    // Detect iOS Safari
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOSSafari) {
      // Prevent elastic scrolling on iOS
      const preventTouchMove = (e) => {
        // Allow scrolling within modal content
        let element = e.target;
        while (element && element !== document.body) {
          if (element.classList.contains('modal-scroll-container')) {
            return; // Allow scrolling in modal
          }
          element = element.parentElement;
        }
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      // Store reference for cleanup
      this._iosPreventTouchMove = preventTouchMove;
    }
  }

  /**
   * Clean up iOS specific event listeners
   * @private
   */
  _cleanupIOSScrollLock() {
    if (this._iosPreventTouchMove) {
      document.removeEventListener('touchmove', this._iosPreventTouchMove);
      this._iosPreventTouchMove = null;
    }
  }
}

// Create singleton instance
const scrollLockManager = new ScrollLockManager();

export default scrollLockManager;